import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import VideoPreviewPlayer from '../../components/video-preview/VideoPreviewPlayer';
import {
  enqueueProVideoRender,
  getVideo,
  getVideoTimeline,
  listMediaAssets,
  listVideoRenders,
  mintPublicAssetLink,
  updateVideoTimeline
} from '../../services/contentAiService';

function clampNumber(v, { min, max, fallback }) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export default function VideoEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const videoId = Number(id || 0) || 0;

  const [loading, setLoading] = useState(false);
  const [video, setVideo] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [renderSettings, setRenderSettings] = useState({
    preset: 'youtube',
    width: 1920,
    height: 1080,
    fps: 30,
    transitionFrames: 12,
    subtitlesEnabled: true,
    waveform: false,
    musicVolume: 0.18,
    voiceVolume: 1.0,
    theme: {
      vignette: true,
      captionFontSize: 44
    }
  });
  const [musicAssetId, setMusicAssetId] = useState('');
  const [partialStart, setPartialStart] = useState('');
  const [partialEnd, setPartialEnd] = useState('');

  const [assetsImage, setAssetsImage] = useState([]);
  const [assetsAudio, setAssetsAudio] = useState([]);
  const [selectedImageAssetId, setSelectedImageAssetId] = useState('');

  const [renders, setRenders] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');

  const loadAll = useCallback(async () => {
    if (!videoId) return;
    setLoading(true);
    try {
      const [vResp, tResp, imgsResp, audResp, rResp] = await Promise.all([
        getVideo(videoId),
        getVideoTimeline(videoId),
        listMediaAssets('image', 200),
        listMediaAssets('audio', 200),
        listVideoRenders(videoId)
      ]);
      const v = vResp?.data || null;
      const t = tResp?.data || null;
      setVideo(v);
      setTimeline(Array.isArray(t?.timelineJson) ? t.timelineJson : []);
      setRenderSettings((prev) => ({
        ...prev,
        ...(t?.renderSettingsJson && typeof t.renderSettingsJson === 'object' ? t.renderSettingsJson : {})
      }));
      setMusicAssetId(t?.musicAssetId ? String(t.musicAssetId) : '');

      setAssetsImage(Array.isArray(imgsResp?.data) ? imgsResp.data : []);
      setAssetsAudio(Array.isArray(audResp?.data) ? audResp.data : []);
      setRenders(Array.isArray(rResp?.data) ? rResp.data : []);

      if (v?.outputVideoAssetId) {
        const link = await mintPublicAssetLink(v.outputVideoAssetId, 120);
        setVideoUrl(link?.data?.url || '');
      } else {
        setVideoUrl('');
      }
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar editor');
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!videoId) return undefined;
    const iv = setInterval(async () => {
      try {
        const rResp = await listVideoRenders(videoId);
        const list = Array.isArray(rResp?.data) ? rResp.data : [];
        setRenders(list);
      } catch (_) {}
    }, 3000);
    return () => clearInterval(iv);
  }, [videoId]);

  const imageById = useMemo(() => {
    return new Map((assetsImage || []).map((a) => [String(a.id), a]));
  }, [assetsImage]);

  const addScene = () => {
    const aid = Number(selectedImageAssetId || 0) || 0;
    if (!aid) {
      toast.error('Selecione uma imagem');
      return;
    }
    setTimeline((prev) => ([
      ...(Array.isArray(prev) ? prev : []),
      {
        assetId: aid,
        durationMs: 4000,
        transitionFrames: clampNumber(renderSettings.transitionFrames, { min: 0, max: 60, fallback: 12 }),
        objectFit: 'cover',
        kenBurns: { scaleFrom: 1.02, scaleTo: 1.12, panX: 0.03, panY: -0.02 }
      }
    ]));
  };

  const saveTimeline = async () => {
    setLoading(true);
    try {
      await updateVideoTimeline(videoId, {
        timelineJson: timeline,
        renderSettingsJson: renderSettings,
        musicAssetId: musicAssetId ? Number(musicAssetId) : null
      });
      toast.success('Timeline salva');
      await loadAll();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const enqueueRender = async () => {
    if (!timeline.length) {
      toast.error('Timeline vazia');
      return;
    }
    if (!video?.voiceAssetId) {
      toast.error('Vídeo sem narração (voiceAssetId)');
      return;
    }
    setLoading(true);
    try {
      const imageAssetIds = Array.from(new Set(timeline.map((s) => Number(s.assetId || 0)).filter(Boolean)));
      await enqueueProVideoRender(videoId, {
        imageAssetIds,
        renderSettingsJson: renderSettings,
        musicAssetId: musicAssetId ? Number(musicAssetId) : null
      });
      toast.success('Render enfileirado');
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar render');
    } finally {
      setLoading(false);
    }
  };

  const enqueuePreview = async () => {
    if (!timeline.length) {
      toast.error('Timeline vazia');
      return;
    }
    setLoading(true);
    try {
      const imageAssetIds = Array.from(new Set(timeline.map((s) => Number(s.assetId || 0)).filter(Boolean)));
      await enqueueProVideoRender(videoId, {
        imageAssetIds,
        renderSettingsJson: {
          ...renderSettings,
          width: 854,
          height: 480,
          crf: 28,
          waveform: false
        },
        musicAssetId: musicAssetId ? Number(musicAssetId) : null
      });
      toast.success('Preview enfileirado');
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar preview');
    } finally {
      setLoading(false);
    }
  };

  const enqueuePartial = async () => {
    const s = Number(partialStart);
    const e = Number(partialEnd);
    if (!Number.isFinite(s) || !Number.isFinite(e)) {
      toast.error('Informe start/end');
      return;
    }
    setLoading(true);
    try {
      const imageAssetIds = Array.from(new Set(timeline.map((x) => Number(x.assetId || 0)).filter(Boolean)));
      await enqueueProVideoRender(videoId, {
        imageAssetIds,
        renderSettingsJson: {
          ...renderSettings,
          partialSceneRange: { startIndex: s, endIndex: e }
        },
        musicAssetId: musicAssetId ? Number(musicAssetId) : null
      });
      toast.success('Render parcial enfileirado');
    } catch (e2) {
      toast.error(e2?.message || 'Falha ao enfileirar parcial');
    } finally {
      setLoading(false);
    }
  };

  const rendersColumns = [
    { field: 'id', headerName: 'Render', width: 100 },
    { field: 'status', headerName: 'Status', width: 140 },
    { field: 'progress', headerName: 'Progresso', width: 140, valueGetter: (p) => {
      const pct = Number(p?.row?.metaJson?.progress?.pct || 0) || 0;
      return `${Math.round(pct * 100)}%`;
    } },
    { field: 'startedAt', headerName: 'Início', width: 180, valueGetter: (p) => (p?.row?.startedAt ? new Date(p.row.startedAt).toLocaleString() : '-') },
    { field: 'finishedAt', headerName: 'Fim', width: 180, valueGetter: (p) => (p?.row?.finishedAt ? new Date(p.row.finishedAt).toLocaleString() : '-') },
    { field: 'errorMessage', headerName: 'Erro', flex: 1, minWidth: 220, valueGetter: (p) => (p?.row?.errorMessage ? String(p.row.errorMessage).slice(0, 120) : '') }
  ];

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Box>
          <Typography variant="h5">Editor de Timeline</Typography>
          <Typography variant="body2" color="text.secondary">
            Vídeo #{videoId} {video?.title ? `- ${video.title}` : ''}
          </Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="outlined" onClick={() => navigate('/videos')}>Voltar</Button>
          <Button variant="outlined" onClick={loadAll} disabled={loading}>Atualizar</Button>
        </Box>
      </Box>

      {videoUrl ? (
        <Box>
          <Typography variant="subtitle1">Preview do último render</Typography>
          <video controls src={videoUrl} style={{ width: '100%', borderRadius: 8 }} />
        </Box>
      ) : null}

      <Box>
        <Typography variant="subtitle1">Preview profissional (HLS + snapshots)</Typography>
        <VideoPreviewPlayer videoId={videoId} title={video?.title || null} />
      </Box>

      <Divider />

      <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
        <Box display="grid" gap={2}>
          <Typography variant="h6">Adicionar cenas</Typography>
          <FormControl fullWidth>
            <InputLabel>Imagem</InputLabel>
            <Select label="Imagem" value={selectedImageAssetId} onChange={(e) => setSelectedImageAssetId(e.target.value)}>
              <MenuItem value="">Selecione</MenuItem>
              {assetsImage.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>{`#${a.id} - ${a.fileName || 'image'}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={addScene} disabled={loading}>Adicionar</Button>

          <Typography variant="h6">Config render</Typography>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField label="Preset" value={renderSettings.preset || 'youtube'} onChange={(e) => setRenderSettings((p) => ({ ...p, preset: e.target.value }))} fullWidth />
            <TextField label="FPS" type="number" value={renderSettings.fps} onChange={(e) => setRenderSettings((p) => ({ ...p, fps: e.target.value }))} fullWidth />
            <TextField label="Width" type="number" value={renderSettings.width} onChange={(e) => setRenderSettings((p) => ({ ...p, width: e.target.value }))} fullWidth />
            <TextField label="Height" type="number" value={renderSettings.height} onChange={(e) => setRenderSettings((p) => ({ ...p, height: e.target.value }))} fullWidth />
            <TextField label="Transition frames" type="number" value={renderSettings.transitionFrames} onChange={(e) => setRenderSettings((p) => ({ ...p, transitionFrames: e.target.value }))} fullWidth />
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={renderSettings.subtitlesEnabled !== false} onChange={(e) => setRenderSettings((p) => ({ ...p, subtitlesEnabled: e.target.checked }))} />
              <Typography>Legendas</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={renderSettings.waveform === true} onChange={(e) => setRenderSettings((p) => ({ ...p, waveform: e.target.checked }))} />
              <Typography>Waveform</Typography>
            </Box>
            <TextField label="Music volume" type="number" value={renderSettings.musicVolume} onChange={(e) => setRenderSettings((p) => ({ ...p, musicVolume: e.target.value }))} fullWidth />
            <TextField label="Voice volume" type="number" value={renderSettings.voiceVolume} onChange={(e) => setRenderSettings((p) => ({ ...p, voiceVolume: e.target.value }))} fullWidth />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Música (opcional)</InputLabel>
            <Select label="Música (opcional)" value={musicAssetId} onChange={(e) => setMusicAssetId(e.target.value)}>
              <MenuItem value="">Sem música</MenuItem>
              {assetsAudio.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>{`#${a.id} - ${a.fileName || 'audio'}`}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="outlined" onClick={saveTimeline} disabled={loading}>Salvar timeline</Button>
            <Button variant="contained" onClick={enqueueRender} disabled={loading}>Renderizar (Remotion)</Button>
            <Button variant="outlined" onClick={enqueuePreview} disabled={loading}>Preview rápido</Button>
          </Box>

          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} alignItems="center">
            <TextField label="Parcial start" type="number" value={partialStart} onChange={(e) => setPartialStart(e.target.value)} fullWidth />
            <TextField label="Parcial end" type="number" value={partialEnd} onChange={(e) => setPartialEnd(e.target.value)} fullWidth />
            <Button variant="outlined" onClick={enqueuePartial} disabled={loading}>Render parcial</Button>
          </Box>
        </Box>

        <Box display="grid" gap={2}>
          <Typography variant="h6">Timeline</Typography>
          <DragDropContext
            onDragEnd={(result) => {
              if (!result.destination) return;
              setTimeline((prev) => reorder(prev, result.source.index, result.destination.index));
            }}
          >
            <Droppable droppableId="timeline">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {(timeline || []).map((scene, idx) => {
                    const aid = String(scene.assetId || '');
                    const asset = imageById.get(aid);
                    return (
                      <Draggable key={`${aid}-${idx}`} draggableId={`${aid}-${idx}`} index={idx}>
                        {(p) => (
                          <Box
                            ref={p.innerRef}
                            {...p.draggableProps}
                            {...p.dragHandleProps}
                            sx={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2, p: 2, mb: 1 }}
                            display="grid"
                            gap={1}
                          >
                            <Typography variant="subtitle2">{idx + 1}. {asset?.fileName || `Asset #${aid}`}</Typography>
                            <Box display="grid" gap={1} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
                              <TextField
                                label="Duração (ms)"
                                type="number"
                                value={scene.durationMs}
                                onChange={(e) => {
                                  const v = Number(e.target.value || 0);
                                  setTimeline((prev) => prev.map((s, i) => (i === idx ? { ...s, durationMs: v } : s)));
                                }}
                                fullWidth
                              />
                              <TextField
                                label="Transition frames"
                                type="number"
                                value={scene.transitionFrames != null ? scene.transitionFrames : renderSettings.transitionFrames}
                                onChange={(e) => {
                                  const v = Number(e.target.value || 0);
                                  setTimeline((prev) => prev.map((s, i) => (i === idx ? { ...s, transitionFrames: v } : s)));
                                }}
                                fullWidth
                              />
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setTimeline((prev) => prev.filter((_, i) => i !== idx))}
                            >
                              Remover
                            </Button>
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>

      <Divider />

      <Box display="grid" gap={2}>
        <Typography variant="h6">Monitor de Render</Typography>
        <DataTable
          rows={renders}
          loading={loading}
          columns={rendersColumns}
          getRowId={(r) => r.id}
        />
      </Box>
    </Box>
  );
}
